import {LogoutOutlined, SettingOutlined} from '@ant-design/icons';
import {Dropdown, type MenuProps} from 'antd';
import {observer} from 'mobx-react';
import {useState} from 'react';
import styled from 'styled-components';
import AppStore from '../../app.store';
import {ProfileAvatar, ProfileIcon} from '../../styles/profile-icon';
import {AssistantIcon} from './assistant-icon';

interface SubMenuProps {
  onLogout: () => void;
  onOpenProfile: () => void;
  onOpenAssistant?: () => void;
  showAssistant?: boolean;
}

const DropdownPanel = styled.div`
  background: var(--card);
  border: 1px solid var(--shell-divider);
  border-radius: 12px;
  box-shadow: var(--card-shadow-lg);
  overflow: hidden;
  min-width: 220px;
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  user-select: none;
  border-bottom: 1px solid var(--shell-divider-soft);
`;

const UserName = styled.span`
  font-weight: 500;
  font-size: 15px;
  color: var(--foreground);
  line-height: 1.3;
`;

const StyledMenu = styled.div`
  .ant-dropdown-menu {
    background: transparent;
    box-shadow: none;
    padding: 4px 8px 8px;
  }

  .ant-dropdown-menu-item {
    border-radius: 6px;
    min-height: 36px;
    padding: 6px 12px !important;
  }

  .ant-dropdown-menu-item:hover {
    background: var(--nav-item-hover-bg) !important;
  }

  .ant-dropdown-menu-item-danger {
    color: var(--destructive) !important;
  }

  .ant-dropdown-menu-item-danger:hover {
    background: var(--nav-item-hover-bg) !important;
  }

  .ant-dropdown-menu-item-divider {
    margin: 4px 0;
    background: var(--shell-divider-soft);
  }
`;

const ProfileMenu = observer(({
  onLogout,
  onOpenProfile,
  onOpenAssistant,
  showAssistant,
}: SubMenuProps) => {
  const {userData} = AppStore;
  const [open, setOpen] = useState(false);

  const menuItems: MenuProps['items'] = [
    ...(showAssistant && onOpenAssistant
      ? [{
          key: 'assistant',
          label: 'Помощник',
          icon: <AssistantIcon size={16} color="var(--muted-foreground)" />,
        }]
      : []),
    {
      key: 'profile',
      label: 'Профиль',
      icon: <SettingOutlined />,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: 'Выход',
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({key}) => {
    setOpen(false);
    if (key === 'assistant') {
      onOpenAssistant?.();
    } else if (key === 'profile') {
      onOpenProfile();
    } else if (key === 'logout') {
      onLogout();
    }
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      menu={{items: menuItems, onClick: handleMenuClick}}
      dropdownRender={(menu) => (
        <DropdownPanel>
          <UserHeader>
            <ProfileAvatar>{userData?.name[0]}</ProfileAvatar>
            <UserName>{userData?.full_name}</UserName>
          </UserHeader>
          <StyledMenu>{menu}</StyledMenu>
        </DropdownPanel>
      )}
    >
      <ProfileIcon
        as="button"
        type="button"
        centered
        aria-label="Меню профиля"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {userData?.name[0]}
      </ProfileIcon>
    </Dropdown>
  );
});

export default ProfileMenu;
